import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { 
  FiAlertCircle, 
  FiCalendar, 
  FiTag, 
  FiPlus, 
  FiX,
  FiArrowRight,
  FiFileText,
  FiLoader
} from "react-icons/fi";
import type { RcaCreateRequest } from "../../../types/rca.types.js";
import { complaintApi } from "../../services/complaintApi.js";
import type { ComplaintSummaryDto } from "../../../types/complaint.types.js";

export type RcaInitiateWizardProps = {
  complaintId?: string;
  complaintCode?: string;
  complaintTitle?: string;
  onContinueToMethod: (data: Partial<RcaCreateRequest> & { complaintCode?: string; complaintTitle?: string }) => void;
  onCancel: () => void;
};

const DEFAULT_TAGS = ["Dimension", "Machining", "Calibration"];

export function RcaInitiateWizard({
  complaintId: initialComplaintId,
  complaintCode: initialComplaintCode,
  complaintTitle: initialComplaintTitle,
  onContinueToMethod,
  onCancel,
}: RcaInitiateWizardProps) {
  // Fetch real complaints from backend database
  const complaintsQuery = useQuery({
    queryKey: ["cmts", "complaints-dropdown"],
    queryFn: async () => {
      const res = await complaintApi.list({ pageSize: 100 });
      return res.items;
    },
    retry: 1,
  });

  const complaints: readonly ComplaintSummaryDto[] = complaintsQuery.data || [];

  const [selectedComplaintId, setSelectedComplaintId] = useState<string>(initialComplaintId || "");
  const [selectedComplaintCode, setSelectedComplaintCode] = useState<string>(initialComplaintCode || "");
  const [selectedComplaintTitle, setSelectedComplaintTitle] = useState<string>(initialComplaintTitle || "");

  const [problemStatement, setProblemStatement] = useState<string>("");
  const [problemDescription, setProblemDescription] = useState<string>("");
  const [severity, setSeverity] = useState<string>("HIGH");
  const [priority, setPriority] = useState<string>("HIGH");
  const [businessImpact, setBusinessImpact] = useState<string>("");
  const [customerImpact, setCustomerImpact] = useState<string>("");
  const [safetyImpact, setSafetyImpact] = useState<string>("None");
  const [targetDate, setTargetDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().split("T")[0]!;
  });
  const [tags, setTags] = useState<string[]>(DEFAULT_TAGS);
  const [newTagInput, setNewTagInput] = useState<string>("");
  const [showAddTag, setShowAddTag] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Auto-select first complaint if none provided
  useEffect(() => {
    if (!selectedComplaintId && complaints.length > 0) {
      const first = complaints[0]!;
      setSelectedComplaintId(first.id);
      setSelectedComplaintCode(first.complaintCode);
      setSelectedComplaintTitle(first.title);
      if (!problemStatement) {
        setProblemStatement(first.title);
      }
      if (first.severity) {
        setSeverity(first.severity.toUpperCase());
      }
    }
  }, [complaints, selectedComplaintId, problemStatement]);

  const handleSelectComplaint = (cmpId: string) => {
    setSelectedComplaintId(cmpId);
    const found = complaints.find(c => c.id === cmpId);
    if (found) {
      setSelectedComplaintCode(found.complaintCode);
      setSelectedComplaintTitle(found.title);
      setProblemStatement(found.title);
      if (found.severity) {
        setSeverity(found.severity.toUpperCase());
      }
    }
  };

  const handleAddTag = () => {
    if (newTagInput.trim() && !tags.includes(newTagInput.trim())) {
      setTags([...tags, newTagInput.trim()]);
      setNewTagInput("");
      setShowAddTag(false);
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  const handleContinue = () => {
    if (!selectedComplaintId) {
      setErrorMsg("Please select a Complaint to associate with this RCA.");
      return;
    }
    if (!problemStatement.trim()) {
      setErrorMsg("Problem statement is required to initiate root cause analysis.");
      return;
    }

    onContinueToMethod({
      complaintId: selectedComplaintId,
      complaintCode: selectedComplaintCode,
      complaintTitle: selectedComplaintTitle,
      problemStatement,
      problemDescription,
      severity,
      priorityLevel: priority,
      customerImpact,
      businessImpact,
      safetyImpact,
      targetDate,
      tags,
    });
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden space-y-6">
      {/* Wizard Header */}
      <div className="p-6 border-b border-slate-100 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider">Step 1 of 2: Define Investigation Scope</div>
          <h2 className="text-xl font-bold text-slate-900 mt-0.5">Create & Initiate RCA Investigation</h2>
          <p className="text-xs text-slate-500 mt-1">
            Select the complaint from database and specify the problem boundary, severity, and investigation target SLA.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold text-xs rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleContinue}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs rounded-lg shadow-sm flex items-center gap-1.5 transition-all"
          >
            Select RCA Method <FiArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Form Content */}
      <div className="p-6 space-y-5">
        {errorMsg && (
          <div className="p-3.5 bg-rose-50 border border-rose-200 rounded-lg text-xs text-rose-700 flex items-center gap-2">
            <FiAlertCircle className="w-4 h-4 shrink-0 text-rose-600" />
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Complaint Selector Dropdown */}
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
          <label className="block text-xs font-bold text-slate-800 flex items-center gap-1.5">
            <FiFileText className="w-4 h-4 text-blue-600" />
            Select Complaint ID <span className="text-rose-500">*</span>
          </label>
          
          {complaintsQuery.isLoading ? (
            <div className="flex items-center gap-2 text-xs text-slate-500 py-2">
              <FiLoader className="w-4 h-4 animate-spin text-blue-600" />
              Loading complaints from database...
            </div>
          ) : (
            <select
              value={selectedComplaintId}
              onChange={(e) => handleSelectComplaint(e.target.value)}
              className="w-full text-xs p-2.5 rounded-lg border border-slate-300 bg-white text-slate-900 font-medium focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- Select a Complaint --</option>
              {complaints.map((cmp) => (
                <option key={cmp.id} value={cmp.id}>
                  {cmp.complaintCode} — {cmp.title} ({cmp.status})
                </option>
              ))}
            </select>
          )}

          {selectedComplaintCode && (
            <div className="text-xs text-slate-500 pt-1 flex items-center justify-between">
              <span>Selected: <strong className="text-slate-800">{selectedComplaintCode}</strong> — {selectedComplaintTitle}</span>
              <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-blue-100 text-blue-800">
                Database Linked
              </span>
            </div>
          )}
        </div>

        {/* Problem Statement */}
        <div className="space-y-1.5">
          <label className="block text-xs font-bold text-slate-700">
            Problem Statement <span className="text-rose-500">*</span>
          </label>
          <textarea
            rows={2}
            value={problemStatement}
            onChange={(e) => setProblemStatement(e.target.value)}
            placeholder="Describe the exact deviation, defect mode, or failure phenomenon observed..."
            className="w-full text-xs p-3 rounded-lg border border-slate-300 bg-white text-slate-900 focus:ring-2 focus:ring-blue-500 font-medium"
          />
        </div>

        {/* Detailed Problem Description */}
        <div className="space-y-1.5">
          <label className="block text-xs font-bold text-slate-700">
            Detailed Problem Background & Context
          </label>
          <textarea
            rows={3}
            value={problemDescription}
            onChange={(e) => setProblemDescription(e.target.value)}
            placeholder="Additional context: batch numbers, shift timing, machine IDs, customer line data..."
            className="w-full text-xs p-3 rounded-lg border border-slate-300 bg-white text-slate-900 focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Priority, Severity & Target Date Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Severity Level:</label>
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
              className="w-full text-xs p-2.5 rounded-lg border border-slate-300 bg-white text-slate-800 font-medium"
            >
              <option value="CRITICAL">Critical (Safety / Legal / Line Stop)</option>
              <option value="HIGH">High (Customer Return / Functional Impact)</option>
              <option value="MEDIUM">Medium (Minor Deviation / Cosmetic)</option>
              <option value="LOW">Low (Insignificant)</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Priority Level:</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="w-full text-xs p-2.5 rounded-lg border border-slate-300 bg-white text-slate-800 font-medium"
            >
              <option value="CRITICAL">Critical</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Target Due Date:</label>
            <div className="relative">
              <input
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className="w-full text-xs p-2.5 rounded-lg border border-slate-300 bg-white text-slate-800 font-semibold"
              />
            </div>
          </div>
        </div>

        {/* Business & Customer Impacts */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Customer Impact:</label>
            <input
              type="text"
              value={customerImpact}
              onChange={(e) => setCustomerImpact(e.target.value)}
              placeholder="e.g. Dissatisfaction, return risk..."
              className="w-full text-xs p-2.5 rounded-lg border border-slate-300 bg-white text-slate-800"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Business Impact:</label>
            <input
              type="text"
              value={businessImpact}
              onChange={(e) => setBusinessImpact(e.target.value)}
              placeholder="e.g. Line stoppage, scrap cost..."
              className="w-full text-xs p-2.5 rounded-lg border border-slate-300 bg-white text-slate-800"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Safety Impact:</label>
            <select
              value={safetyImpact}
              onChange={(e) => setSafetyImpact(e.target.value)}
              className="w-full text-xs p-2.5 rounded-lg border border-slate-300 bg-white text-slate-800"
            >
              <option value="None">None</option>
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
            </select>
          </div>
        </div>

        {/* Tags */}
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1">Tags:</label>
          <div className="flex flex-wrap items-center gap-2">
            {tags.map(tag => (
              <span key={tag} className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 flex items-center gap-1.5">
                <FiTag className="w-3 h-3 text-blue-600" />
                {tag}
                <button
                  type="button"
                  onClick={() => handleRemoveTag(tag)}
                  className="hover:text-rose-600"
                >
                  <FiX className="w-3 h-3" />
                </button>
              </span>
            ))}

            {showAddTag ? (
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  value={newTagInput}
                  onChange={(e) => setNewTagInput(e.target.value)}
                  placeholder="Tag name..."
                  className="text-xs px-2.5 py-1 rounded-lg border border-slate-300 bg-white w-28"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="p-1 bg-blue-600 text-white rounded text-xs"
                >
                  <FiPlus className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowAddTag(true)}
                className="px-2.5 py-1 rounded-full text-xs font-semibold border border-dashed border-slate-300 text-slate-600 hover:border-blue-500 hover:text-blue-600 flex items-center gap-1"
              >
                <FiPlus className="w-3 h-3" /> Add Tag
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
